import { Row } from '@devexpress/dx-react-grid';
import { MainPageContainer, UpperInfoContainer } from 'components/PageLayout';
import { useActiveWebUsageReport } from 'pages/hooks';
import { FormEvent, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AxiosError } from 'axios';
import { createReportURL } from 'core/routes';
import { errorMessages } from 'api/resources/responseMessages/errorMessages';
import { ReportGridContainer } from 'components/ReportGridContainer';
import { NoReportDataMessage } from 'components/ReportsComponents/NoReportDataMessage';
import {
  columns,
  selectOptions,
  ACTIVEWEB_USAGE_REPORT,
  ACTIVEWEB_USAGE_REPORT_GRID_TITLE,
  ACTIVEWEB_USAGE_REPORT_MESSAGE,
  reportHeaderTagLine,
} from './constants';
import { ReportBackButton } from 'components/ReportsComponents/ReportBackButton';
import { ReportHeader } from 'components/ReportsComponents/ReportHeader';
import { ReportSelectForm } from 'components/ReportForms/ReportSelectForm';
import { cellPrepared, customizeCell, valuesConverter } from './helpers';
import { reportLogger } from 'pages/ReportsDetails/reportLogger';
import { ReportProps } from 'pages/ReportsDetails/types';
import DevExtremeReportGrid from 'components/ReportGrid_New';


export function ActiveWebUsageV2({
  reportPath,
  setErrorMessage,
  setToast,
}: Readonly<ReportProps>) {
  const navigate = useNavigate();
  const location = useLocation();
  const urlParams = new URLSearchParams(location.search);
  const urlParamsValue = {
    accountStatus: urlParams.get('accountStatus'),
  };

  const queryEnabled = !!urlParamsValue.accountStatus;

  const { isReportFetching: isBusy, refetchReport, reportData } =
    useActiveWebUsageReport(
      urlParamsValue.accountStatus,
      queryEnabled,
      handleError
    );

  const rows = useMemo<Row[] | undefined>(() => reportData ? valuesConverter(reportData) : undefined, [reportData]);

  const [accountStatus, setAccountStatus] = useState<string | null>(
    urlParamsValue.accountStatus ?? null
  );

  useEffect(() => {
    navigateIfPossible();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlParamsValue.accountStatus]);

  return (
    <MainPageContainer isLoading={isBusy} direction="column" wrap="nowrap">
      <UpperInfoContainer direction="column">
        <ReportBackButton />
        <ReportHeader
          reportName={ACTIVEWEB_USAGE_REPORT}
          infoMessage={ACTIVEWEB_USAGE_REPORT_MESSAGE}
        />
        <ReportSelectForm
          isBusy={isBusy}
          label="Account Status"
          handleSubmit={handleSubmit}
          onChange={setAccountStatus}
          selectOptions={selectOptions}
          selectedValue={accountStatus}
        />
      </UpperInfoContainer>

      {!isBusy && rows?.length === 0 && <NoReportDataMessage />}

      <ReportGridContainer direction="column">
        <DevExtremeReportGrid
          cellPrepared={cellPrepared}
          columns={columns}
          customizeCell={customizeCell}
          displayGroupingPanel
          headerTitleForSheet={ACTIVEWEB_USAGE_REPORT}
          isLoading={isBusy}
          pdfOrientation='l'
          reportHeaderTagLine={reportHeaderTagLine}
          rows={rows}
          title={ACTIVEWEB_USAGE_REPORT_GRID_TITLE}
        />
      </ReportGridContainer>

    </MainPageContainer>
  );

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    navigateIfPossible();
  }

  function navigateIfPossible() {
    if (accountStatus != null)
      if (urlParamsValue.accountStatus == accountStatus) refetchReport();
      else navigate(createReportURL({ accountStatus }, reportPath), { replace: true })
  }

  function handleError(error: AxiosError) {
    reportLogger(error);
    setErrorMessage(errorMessages.reports(ACTIVEWEB_USAGE_REPORT));
    setToast(true);
  }
}
